import { IntlTFunc } from 'next-intl';

import {
  SimpleProductFragment,
  ProductBanner,
  CurrencyEnum
} from '@/fo/api/magento';

import { RATING_SCALE } from '../constants';
import { Categories, MappedProduct, Option, ProductsMindbox } from '../types';
import {
  attributesMapper,
  mediaGalleryMapper,
  breadcrumbsMapper,
  labelsBrandMapper,
  labelsMapper,
  imageMapper,
  getLastLevelCategory,
  getFirstLevelCategory,
  expertImageMapper,
  getProductGroupName
} from '../utils';

export const utilMapSimpleProductData = (
  product: SimpleProductFragment,
  t: IntlTFunc,
  mindboxProducts?: ProductsMindbox
): MappedProduct => {
  const {
    url_key,
    sku: productSku,
    ean,
    price,
    ratingSummary,
    reviewCount,
    detailedRating,
    stockStatus,
    image,
    thumbnail,
    mediaGallery,
    attributes,
    banners,
    special_price,
    price_without_discount,
    id
  } = product;

  const stock = stockStatus ?? null;

  const {
    brand,
    gammas,
    name,
    nameEng,
    shortDescription,
    description,
    usageInstructions,
    composition,
    activeComponents,
    volume,
    characteristics,
    purpose,
    labels,
    effects,
    video,
    mindboxId,
    expert,
    productGroup: productGroupRaw
  } = attributesMapper(attributes);

  const sku = productSku ?? '';

  const basePrice = price_without_discount
    ? price_without_discount
    : (price.maximum_price?.regular_price.value ?? null);
  const finalPrice = price_without_discount
    ? (price.maximum_price?.regular_price.value ?? null)
    : (price.maximum_price?.final_price.value ?? null);

  const currency =
    price.maximum_price?.final_price.currency ?? CurrencyEnum.Rub;
  const configurableVariants: Option[] =
    volume && volume.value !== ''
      ? [
          {
            sku,
            label: volume?.label,
            stockStatus: stock,
            isSelected: true
          }
        ]
      : [];

  const productGroup = productGroupRaw
    ? {
        ...productGroupRaw,
        name: getProductGroupName(productGroupRaw)
      }
    : null;

  const mindboxProductData = mindboxProducts
    ? mindboxProducts[mindboxId || sku]
    : null;

  const firstLevelCategory =
    'categories' in product
      ? getFirstLevelCategory(product.categories as Categories)
      : null;

  const lastLevelCategory =
    'categories' in product
      ? getLastLevelCategory(product.categories as Categories)
      : null;

  const bestRating =
    detailedRating?.reduce((acc, item) => {
      if (item?.rating && item?.percent) {
        acc = item.rating > acc ? item.rating : acc;
      }
      return acc;
    }, 0) ?? 0;

  const worstRating =
    detailedRating?.reduce((acc, item) => {
      if (item?.rating && item?.percent) {
        acc = item.rating < acc || acc === 0 ? item.rating : acc;
      }

      return acc;
    }, 0) ?? 0;

  const getDiscount = (price?: number | null, oldPrice?: number | null) => {
    if (!oldPrice || !price || price === 0 || oldPrice === price) return null;

    return `-${Math.round(100 - (price / oldPrice) * 100)}%`;
  };

  return {
    id,
    sku,
    ean,
    mindboxId,
    name,
    nameEng,
    link: `/product/${url_key}`,
    shortDescription,
    specialPrice: special_price ?? null,
    basePrice,
    currency,
    volume: volume?.label,
    finalPrice,
    firstLevelCategory,
    lastLevelCategory,
    breadcrumbs: breadcrumbsMapper(name, lastLevelCategory, t),
    labelsBonus: mindboxProductData?.bonusLabels ?? [],
    labelsBrand: labelsBrandMapper(brand, gammas),
    brand: brand?.label ?? '',
    catalogBrandUrl: brand?.url ?? '',
    series: gammas.at(0)?.label ?? '',
    labels: labels
      ? labelsMapper(labels, mindboxProductData?.discountLabels ?? [])
      : [],
    baseImage: image ? imageMapper(image) : null,
    thumbnail: thumbnail ? imageMapper(thumbnail) : null,
    mediaGallery: mediaGalleryMapper(mediaGallery),
    rating: Math.round(((RATING_SCALE * ratingSummary) / 100) * 10) / 10,
    bestRating,
    worstRating,
    reviewCount,
    stockStatus: stock,
    stockQuantity: product?.quantity || 0,
    configurableVariants,
    effects,
    video: video || undefined,
    bonusPoints: mindboxProductData?.bonusPoints ?? 0,
    bonusPointsMax: mindboxProductData?.bonusPointsMax ?? 0,

    purpose,

    description,
    characteristics: [
      {
        label: t('product.characteristics.article'),
        value: ean
      },
      ...characteristics
    ],
    activeComponents: activeComponents ? activeComponents.split(';') : [],
    composition,
    usageInstructions,
    banners: banners as ProductBanner[],
    expert,
    expertImage: expertImageMapper(mediaGallery),

    productGroup,
    discount: getDiscount(finalPrice, basePrice)
  };
};
